import { Component, FC, useCallback } from 'react';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import {
  TypeFilterType,
  TypeSingleFilterValue,
} from '@inovua/reactdatagrid-community/types';

import {
  ExtendedParticipant,
  ExtendedParticipantField,
  ParticipantFieldCategory,
  ParticipantFieldType,
  useCountries,
  useExtendedParticipantFieldsByCategory,
} from '@app/api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import StringFilter from '@inovua/reactdatagrid-community/StringFilter';
import SelectFilter from '@inovua/reactdatagrid-community/SelectFilter';
import DateFilter from '@inovua/reactdatagrid-community/DateFilter';
import NumberFilter from '@inovua/reactdatagrid-community/NumberFilter';
import BooleanFilter from '@inovua/reactdatagrid-community/BoolFilter';
import moment from 'moment-timezone';
import {
  GeolocationFilter,
  GeolocationFilterType,
} from '../components/GeolocationFilter';

const categories = Object.values(ParticipantFieldCategory);

const filterByFieldType: Record<
  ParticipantFieldType,
  | 'string'
  | 'multi_select'
  | 'date'
  | 'number'
  | 'age'
  | 'boolean'
  | 'geolocation'
> = {
  [ParticipantFieldType.EMAIL]: 'string',
  [ParticipantFieldType.PHONE_NUMBER]: 'string',

  [ParticipantFieldType.SINGLE_CHOICE]: 'multi_select',
  [ParticipantFieldType.MULTIPLE_CHOICE]: 'multi_select',
  [ParticipantFieldType.TAGS]: 'multi_select',

  [ParticipantFieldType.SHORT_TEXT]: 'string',
  [ParticipantFieldType.LONG_TEXT]: 'string',
  [ParticipantFieldType.DATE]: 'date',
  [ParticipantFieldType.NUMBER]: 'number',

  [ParticipantFieldType.GEOLOCATION]: 'geolocation',
  [ParticipantFieldType.INCOME]: 'multi_select',
  [ParticipantFieldType.TIMEZONE]: 'multi_select',
  [ParticipantFieldType.LOCALES]: 'multi_select',
  [ParticipantFieldType.BOOLEAN]: 'boolean',
};

export const filterTypes: Record<
  string,
  TypeFilterType & { defaultFilter: string }
> = {
  uuid: {
    type: 'string',
    emptyValue: '',
    defaultFilter: 'eq',
    operators: [
      {
        name: 'eq',
        fn: ({ value, filterValue }) => {
          return value === filterValue;
        },
      },
      {
        name: 'neq',
        fn: ({ value, filterValue }) => {
          return value !== filterValue;
        },
      },
    ],
  },
  multi_select: {
    type: 'string',
    emptyValue: null,
    defaultFilter: 'inlist',
    operators: [
      {
        name: 'inlist',
        fn: ({ value, filterValue }) => {
          if (!filterValue) {
            return true;
          }
          if (!value) {
            return false;
          }
          if (Array.isArray(value)) {
            return value.some((v) => filterValue.includes(v));
          }
          return filterValue.includes(value);
        },
      },
      {
        name: 'notinlist',
        fn: ({ value, filterValue }) => {
          if (!filterValue) {
            return true;
          }
          if (!value) {
            return false;
          }
          if (Array.isArray(value)) {
            return !value.some((v) => filterValue.includes(v));
          }
          return !filterValue.includes(value);
        },
      },
      {
        name: 'empty',
        fn: ({ value }) => {
          return !value || (Array.isArray(value) && value.length === 0);
        },
      },
      {
        name: 'notEmpty',
        fn: ({ value }) => {
          return !!value && (!Array.isArray(value) || value.length !== 0);
        },
      },
    ],
  },
  string: {
    ...ReactDataGrid.defaultProps.filterTypes.string,
    defaultFilter: 'Search',

    operators: [
      {
        name: 'Search',
        fn: ({ value, filterValue }) => {
          return value?.toLowerCase().includes(filterValue?.toLowerCase());
        },
      },
      ...ReactDataGrid.defaultProps.filterTypes.string.operators,
    ],
  },
  date: {
    ...ReactDataGrid.defaultProps.filterTypes.date,
    defaultFilter: 'inrange',
    operators: [
      ...ReactDataGrid.defaultProps.filterTypes.date.operators,
      {
        name: 'empty',
        fn: ({ value }) => !value,
      },
      {
        name: 'notEmpty',
        fn: ({ value }) => !!value,
      },
    ],
  },
  number: {
    ...ReactDataGrid.defaultProps.filterTypes.number,
    defaultFilter: 'eq',
    operators: [
      ...ReactDataGrid.defaultProps.filterTypes.number.operators,
      {
        name: 'empty',
        fn: ({ value }) => !value && value !== 0,
      },
      {
        name: 'notEmpty',
        fn: ({ value }) => !!value || value === 0,
      },
    ],
  },
  boolean: {
    ...ReactDataGrid.defaultProps.filterTypes.boolean,
    defaultFilter: 'eq',
  },
  geolocation: {
    type: 'geolocation',
    emptyValue: null,
    operators: [
      {
        name: 'dist',
        fn: ({
          data,
          filterValue,
        }: {
          data: ExtendedParticipant;
          filterValue?: GeolocationFilterType;
        }) => {
          if (!data.location || !data.location.lat) {
            return false;
          }
          if (!filterValue?.location) {
            return true;
          }
          const { lat: lat1, lon: lon1 } = filterValue.location;
          const { lat: lat2, lon: lon2 } = data.location;
          const R = 6371; // metres
          const phi1 = (lat1 * Math.PI) / 180; // phi, λ in radians
          const phi2 = (lat2 * Math.PI) / 180;
          const LambdaPhi = ((lat2 - lat1) * Math.PI) / 180;
          const lambda = ((lon2 - lon1) * Math.PI) / 180;

          const a =
            Math.sin(LambdaPhi / 2) * Math.sin(LambdaPhi / 2) +
            Math.cos(phi1) *
              Math.cos(phi2) *
              Math.sin(lambda / 2) *
              Math.sin(lambda / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

          const d = R * c; // in metres
          return d <= (filterValue.radius || 100);
        },
      } as any,
    ],
    defaultFilter: 'dist',
  },
  age: {
    ...ReactDataGrid.defaultProps.filterTypes.date,
    defaultFilter: 'inrange',
    operators: [
      {
        name: 'lt',
        fn: ({ value, filterValue }) =>
          moment(value).isAfter(
            moment().subtract(filterValue, 'years').startOf('day'),
          ),
      },
      {
        name: 'lte',
        fn: ({ value, filterValue }) =>
          moment(value).isAfter(
            moment()
              .subtract(filterValue + 1, 'years')
              .startOf('day'),
          ),
      },
      {
        name: 'gt',
        fn: ({ value, filterValue }) =>
          moment(value).isBefore(
            moment()
              .subtract(filterValue + 1, 'years')
              .startOf('day'),
          ),
      },
      {
        name: 'gte',
        fn: ({ value, filterValue }) =>
          moment(value).isBefore(
            moment().subtract(filterValue, 'years').startOf('day'),
          ),
      },
      {
        name: 'eq',
        fn: ({ value, filterValue }) =>
          moment(value).isBetween(
            moment()
              .subtract(filterValue + 1, 'years')
              .startOf('day'),
            moment().subtract(filterValue, 'years').startOf('day'),
          ),
      },
      {
        name: 'neq',
        fn: ({ value, filterValue }) =>
          !moment(value).isBetween(
            moment()
              .subtract(filterValue + 1, 'years')
              .startOf('day'),
            moment().subtract(filterValue, 'years').startOf('day'),
          ),
      },
      {
        name: 'inrange',
        fn: ({ value, filterValue }) =>
          moment(value).isBetween(
            filterValue.end
              ? moment()
                  .subtract(filterValue.end + 1, 'years')
                  .startOf('day')
              : '1970-01-01',
            filterValue.start
              ? moment().subtract(filterValue.start, 'years').startOf('day')
              : undefined,
          ),
      },
      {
        name: 'notinrange',
        fn: ({ value, filterValue }) =>
          !moment(value).isBetween(
            filterValue.end
              ? moment()
                  .subtract(filterValue.end + 1, 'years')
                  .startOf('day')
              : '1970-01-01',
            filterValue.start
              ? moment().subtract(filterValue.start, 'years').startOf('day')
              : undefined,
          ),
      },
      {
        name: 'empty',
        fn: ({ value }) => !value,
      },
      {
        name: 'notEmpty',
        fn: ({ value }) => !!value,
      },
    ],
  },
};

export const useDefaultParticipantFilters = () => {
  const participantFields = useExtendedParticipantFieldsByCategory();
  return useMemo(() => {
    if (!participantFields || Object.keys(participantFields).length === 0) {
      return null;
    }
    const res = categories.reduce(
      (acc: TypeSingleFilterValue[], category: string) => {
        if (!participantFields) {
          return acc;
        }
        const fields = participantFields[category as string];

        return acc.concat(
          fields?.map((field) => {
            let filterType = filterByFieldType[field.type];
            if (
              field.type === ParticipantFieldType.DATE &&
              field.displayAsAge
            ) {
              filterType = 'age';
            }

            return {
              name: field.name,
              operator: filterTypes[filterType].defaultFilter,
              type: filterType,
              value: filterTypes[filterType].emptyValue,
            };
          }) || [],
        );
      },
      [] as TypeSingleFilterValue[],
    );
    res.unshift({
      name: 'id',
      operator: 'eq',
      type: 'uuid',
      value: '',
    });
    return res;
  }, [participantFields]);
};

interface FilterComp {
  filterType?: string;
  filterName?: string;
  filterEditor?: typeof Component | FC;
  filterEditorProps?: any;
  filterDelay?: number;
}

const editorByFilterType = {
  string: StringFilter,
  multi_select: SelectFilter,
  date: DateFilter,
  number: NumberFilter,
  age: NumberFilter,
  boolean: BooleanFilter,
  geolocation: GeolocationFilter,
};

export const useGetFiltersComp = (): ((
  field: ExtendedParticipantField,
) => FilterComp) => {
  const { data: countries } = useCountries();

  return useCallback(
    (field: ExtendedParticipantField) => {
      let type = filterByFieldType[field.type];
      if (field.type === ParticipantFieldType.DATE && field.displayAsAge) {
        type = 'age';
      }
      const options: FilterComp = {
        filterType: type,
        filterEditor: editorByFilterType[type],
      };

      if (field.choices) {
        options.filterEditorProps = {
          dataSource: field.choices?.map((choice) => ({
            id: choice.value,
            label: choice.label,
          })),
          multiple: true,
        };
      }

      if (field.type === ParticipantFieldType.TIMEZONE) {
        options.filterEditorProps = {
          dataSource: moment.tz.names().map((tz) => ({
            id: tz,
            label: tz,
          })),
          multiple: true,
        };
      }

      return options;
    },
    [countries],
  );
};
